import LinkifyIt from 'linkify-it';

// Only schemes we are willing to render as clickable anchors. Mirrors the
// `allowed-schemas` passed to <Letter> when rendering email HTML.
const ALLOWED_SCHEMES = ['http', 'https', 'mailto'];

// Text inside these elements is never linkified: <a> would produce nested
// anchors, the rest are either code samples or non rendered content.
const SKIPPED_TAGS = [
  'A',
  'CODE',
  'PRE',
  'SCRIPT',
  'STYLE',
  'TEXTAREA',
  'TITLE',
];

// Both fuzzy options are off, so only text carrying an explicit scheme is
// linkified:
// - `fuzzyLink` would turn any prose mentioning a domain into a link, which is
//   common in email footers and signatures ("E-mail odeslán z Higarden.cz").
// - `fuzzyEmail` would turn bare email addresses into mailto links. It also
//   only fires for addresses on a known TLD, so `a@b.com` would linkify while
//   `a@b.xyz` would not - inconsistent enough to not be worth having.
// An explicitly typed `mailto:someone@example.com` is still linkified, since
// that is a url rather than a guess about an address.
const linkify = new LinkifyIt().set({
  fuzzyLink: false,
  fuzzyEmail: false,
  fuzzyIP: false,
});

const isSkipped = textNode => {
  let node = textNode.parentNode;
  while (node && node.nodeType === Node.ELEMENT_NODE) {
    if (SKIPPED_TAGS.includes(node.tagName)) return true;
    node = node.parentNode;
  }
  return false;
};

const isAllowedUrl = url => {
  const scheme = String(url).split(':')[0].toLowerCase();
  return ALLOWED_SCHEMES.includes(scheme);
};

// Elements that end the current line of text. Anything else is treated as
// inline, so text flowing out of it continues the same visual sentence.
const BLOCK_TAGS = [
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BR',
  'DIV',
  'DL',
  'FIELDSET',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'UL',
];

/**
 * True when text immediately continues after this node with no whitespace,
 * i.e. the node ends mid word. Editors such as Outlook routinely split a run
 * of text across inline <span>s, and linkifying the leading half of a url
 * would produce a clickable link to a truncated host.
 */
const continuesWithoutWhitespace = node => {
  // Climb out of inline wrappers the node sits at the end of, so
  // `<span>https://exa</span>mple.com` is seen as continuing too.
  let current = node;
  while (current && !current.nextSibling) {
    const parent = current.parentNode;
    if (
      !parent ||
      parent.nodeType !== Node.ELEMENT_NODE ||
      BLOCK_TAGS.includes(parent.tagName)
    ) {
      return false;
    }
    current = parent;
  }

  const next = current?.nextSibling;
  if (!next) return false;
  if (
    next.nodeType === Node.ELEMENT_NODE &&
    BLOCK_TAGS.includes(next.tagName)
  ) {
    return false;
  }

  const text = next.textContent || '';
  return text.length > 0 && !/^\s/.test(text);
};

const buildFragment = (doc, text, matches) => {
  const fragment = doc.createDocumentFragment();
  let cursor = 0;

  matches.forEach(match => {
    if (match.index > cursor) {
      fragment.appendChild(doc.createTextNode(text.slice(cursor, match.index)));
    }

    const anchor = doc.createElement('a');
    anchor.setAttribute('href', match.url);
    anchor.setAttribute('class', 'link');
    anchor.textContent = match.text;
    fragment.appendChild(anchor);

    cursor = match.lastIndex;
  });

  if (cursor < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(cursor)));
  }

  return fragment;
};

/**
 * Wraps bare URLs found in the text nodes of a DOM tree in anchor elements.
 * Mutates the tree in place.
 *
 * `rel` and `target` are intentionally not set: email HTML is re-sanitized by
 * lettersanitizer, which forces `rel="noopener noreferrer"` and
 * `target="_blank"` on every anchor.
 *
 * @param {Element} root - element whose descendants should be linkified
 */
export const linkifyElement = root => {
  if (!root) return;

  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const pending = [];

  let textNode = walker.nextNode();
  while (textNode) {
    const text = textNode.nodeValue;
    if (text && text.trim() && !isSkipped(textNode)) {
      const matches = (linkify.match(text) || []).filter(match =>
        isAllowedUrl(match.url)
      );
      // A match running to the very end of a node that continues mid word is
      // a url split across elements, so only part of it is visible here.
      const last = matches[matches.length - 1];
      if (
        last &&
        last.lastIndex === text.length &&
        continuesWithoutWhitespace(textNode)
      ) {
        matches.pop();
      }
      if (matches.length) pending.push({ textNode, text, matches });
    }
    textNode = walker.nextNode();
  }

  // Replace only after the walk, so mutations do not invalidate the walker.
  pending.forEach(({ textNode: node, text, matches }) => {
    node.parentNode.replaceChild(buildFragment(doc, text, matches), node);
  });
};

export default linkifyElement;
